import React, { useState, useEffect, createContext, useContext } from 'react';
import { toast } from 'react-hot-toast';

import {
  fetchBotSteps,
  initializeStepStream,
  streamStepChat,
  callFunction,
  createSession,
  saveStepChat,
} from './../services/api'; // Import the API functions
import { DataState } from './DataProvider';

const DataContext = createContext();

const BotsDataProvider = ({ children }) => {
  const [botId, setBotId] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatInput, setChatInput] = useState({});
  const [chats, setChats] = useState({});
  const [botCurrentChat, setBotCurrentChat] = useState({});
  const [botStreamingResponse, setBotStreamingResponse] = useState({});
  const [responses, setResponses] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const {
    userGoogleId,
    currentTaskId,
    selectedBotId,
    setIsBusy,
    justStartedBot,
    setJustStartedBot,
    botStepInputToSubmit,
    setBotStepInputToSubmit,
    useAutomaticStepSwitching,
    moveToNextStep,
    setMoveToNextStep,
    setBotSessionJustCreatedId,
    currentBotSessionChats,
    currentTaskSessions,
    setCurrentTaskSessions,
    scrollToBottom,
  } = DataState();

  const [currentStepId, setCurrentStepId] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [isAutomatic, setIsAutomatic] = useState(true);
  const [isAutomaticSwitching, setIsAutomaticSwitching] = useState(true);
  const [haveStepResults, setHaveStepResults] = useState(false);
  const [isTest, setIsTest] = useState(true);
  const [stepCompleted, setStepCompleted] = useState(false);
  const [savedStepFinalResponse, setSavedStepFinalResponse] = useState(false);
  const [stepsFinalResponse, setStepsFinalResponse] = useState({});
  const [currentToolResults, setCurrentToolResults] = useState({});
  const [startExecution, setStartExecution] = useState(false);

  const [newBotStepsLoaded, setNewBotStepsLoaded] = useState(false);

  useEffect(() => {
    if (currentBotSessionChats.session_id) {
      setSessionId(currentBotSessionChats.session_id);
      setBotId(currentBotSessionChats.bot_id);
      let last_step_id_with_chat = 0;
      let last_index = 0;
      let lastCompleted = false;

      const temp_step_chats = {};
      const steps_final_response = {};
      const initialChatsForSteps = {};

      for (let i = 0; i < currentBotSessionChats.chats.length; ++i) {
        temp_step_chats[currentBotSessionChats.chats[i].step_id] =
          currentBotSessionChats.chats[i].step_chats;

        if (currentBotSessionChats.chats[i].step_chats.length > 0) {
          last_step_id_with_chat = currentBotSessionChats.chats[i].step_id;
          last_index = currentBotSessionChats.chats[i].step_index;
          lastCompleted = false;
          for (let chat of currentBotSessionChats.chats[i].step_chats) {
            if (chat.role === 'bot' && chat.type === 'step_response') {
              steps_final_response[currentBotSessionChats.chats[i].step_id] =
                chat.content;
              lastCompleted = true;
            }
          }
          if (
            currentBotSessionChats.chats[i].step_chats[
              currentBotSessionChats.chats[i].step_chats.length - 1
            ].role === 'user'
          ) {
            initialChatsForSteps[currentBotSessionChats.chats[i].step_id] =
              currentBotSessionChats.chats[i].step_chats[
                currentBotSessionChats.chats[i].step_chats.length - 1
              ];
          } else {
            initialChatsForSteps[currentBotSessionChats.chats[i].step_id] = {
              role: 'user',
              content: '',
            };
          }
        } else {
          initialChatsForSteps[currentBotSessionChats.chats[i].step_id] = {
            role: 'user',
            content: currentBotSessionChats.chats[i].step_title,
          };
        }
      }
      const initialStreamingResponse = {};
      const progressSteps = currentBotSessionChats.steps.map((step) => {
        initialStreamingResponse[step.step_id] = {
          role: 'assistant',
          content: '',
          tool_calls: [],
        };

        if (step.step_index < last_index) {
          return { ...step, status: 'Completed' };
        } else if (step.step_index === last_index) {
          return { ...step, status: 'In Progress' };
        } else {
          return step;
        }
      });

      setBotStreamingResponse(initialStreamingResponse);
      setBotCurrentChat(initialChatsForSteps);
      setSteps(progressSteps);
      setCurrentStepId(last_step_id_with_chat);
      setCurrentStepIndex(last_index);
      setChats({ ...temp_step_chats });
      setStepsFinalResponse(steps_final_response);

      if (lastCompleted) {
        setStepCompleted(true);
      }
    }
  }, [currentBotSessionChats]);

  useEffect(() => {
    if (moveToNextStep && !isAutomaticSwitching) {
      if (stepsFinalResponse[currentStepId]) {
        setSteps((prevSteps) =>
          prevSteps.map((step) => {
            if (step.step_index === currentStepIndex) {
              return { ...step, status: 'Completed' };
            } else if (step.step_index === currentStepIndex + 1) {
              return { ...step, status: 'In Progress' };
            } else {
              return step;
            }
          }),
        );
        if (currentStepIndex < steps.length - 1) {
          setCurrentStepId(steps[currentStepIndex + 1].id);
          setCurrentStepIndex(currentStepIndex + 1);
          setHaveStepResults(true);
        }
      }
    }
    setMoveToNextStep(false);
    // eslint-disable-next-line
  }, [moveToNextStep]);

  useEffect(() => {
    setIsAutomaticSwitching(useAutomaticStepSwitching);
    // eslint-disable-next-line
  }, [useAutomaticStepSwitching]);

  useEffect(() => {
    if (botStepInputToSubmit) {
      setBotCurrentChat({
        ...botCurrentChat,
        [currentStepId]: { role: 'user', content: botStepInputToSubmit },
      });
      setHaveStepResults(true);
      setBotStepInputToSubmit('');
    }
    // eslint-disable-next-line
  }, [botStepInputToSubmit]);

  useEffect(() => {
    setBotId(selectedBotId);
    // eslint-disable-next-line
  }, [selectedBotId]);

  useEffect(() => {
    if (justStartedBot && newBotStepsLoaded) {
      setStartExecution(true);
      setJustStartedBot(false);
      setNewBotStepsLoaded(false);
    }
    // eslint-disable-next-line
  }, [justStartedBot, newBotStepsLoaded]);

  useEffect(() => {
    if (startExecution) {
      if (botId) {
        if (sessionId && steps.length) {
          for (const step of steps) {
            if (chats[step.step_id]?.length) {
              step.step_chats = chats[step.step_id];
            }
          }
          setCurrentTaskSessions({
            ...currentTaskSessions,
            [sessionId]: steps,
          });
        }

        handleCreateSession();
      }
      setStartExecution(false);
    }
    // eslint-disable-next-line
  }, [startExecution]);

  useEffect(() => {
    if (haveStepResults && isAutomatic) {
      handleStreamStep();
      setHaveStepResults(false);
    }
    // eslint-disable-next-line
  }, [haveStepResults]);

  useEffect(() => {
    if (stepCompleted && isAutomaticSwitching) {
      setSteps((prevSteps) =>
        prevSteps.map((step) => {
          if (step.step_index === currentStepIndex) {
            return { ...step, status: 'Completed' };
          } else if (
            isAutomaticSwitching &&
            step.step_index === currentStepIndex + 1
          ) {
            return { ...step, status: 'In Progress' };
          } else {
            return step;
          }
        }),
      );

      if (
        stepCompleted &&
        currentStepIndex < steps.length - 1 &&
        isAutomaticSwitching
      ) {
        setCurrentStepId(steps[currentStepIndex + 1].id);
        setCurrentStepIndex(currentStepIndex + 1);
        setHaveStepResults(true);
      }
      setStepCompleted(false);
    }
    // eslint-disable-next-line
  }, [stepCompleted]);

  useEffect(() => {
    if (selectedBotId && justStartedBot) {
      const fetchSteps = async () => {
        try {
          setLoading(true);

          const stepsData = await fetchBotSteps(selectedBotId, userGoogleId);
          setSteps(
            stepsData.steps.map((step) =>
              step.step_index === 0
                ? {
                    ...step,
                    status: 'In Progress',
                  }
                : {
                    ...step,
                    status: 'Pending',
                  },
            ),
          );

          const initialStreamingResponse = {};
          const initialChats = {};
          const chats_temp = {};

          stepsData.steps.forEach((step) => {
            initialChats[step.id] = { role: 'user', content: step.step_title };
            initialStreamingResponse[step.id] = {
              role: 'assistant',
              content: '',
              tool_calls: [],
            };
            setChatInput((prev) => ({ ...prev, [step.id]: '' }));
            chats_temp[step.id] = [];
          });

          setBotCurrentChat(initialChats);
          setCurrentStepId(stepsData.steps[0].id);
          setCurrentStepIndex(0);
          setChats(chats_temp);
          setNewBotStepsLoaded(true);
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      };
      fetchSteps();
    }
    // eslint-disable-next-line
  }, [justStartedBot]);

  const handleCreateSession = async () => {
    setError(null);
    try {
      const response = await createSession(
        { bot_id: botId, task_id: currentTaskId },
        userGoogleId,
      );

      if (response.error) {
        setError(response.error);
        return;
      }
      setSessionId(response.session_id);
      setBotSessionJustCreatedId(response.session_id);

      setHaveStepResults(true);
      return response.session_id;
    } catch (error) {
      setError(error?.message || String(error));
    }
  };

  const handleStreamStep = async () => {
    try {
      if (!userGoogleId || !sessionId || !currentStepId || !botId) {
        setError('Session Authentication Error');
        return;
      }
      const stepId = currentStepId;
      if (
        (!botCurrentChat[stepId] || !botCurrentChat[stepId].content) &&
        (!currentToolResults[stepId] || !currentToolResults[stepId].length)
      ) {
        return;
      }

      let newCurrentChat = [botCurrentChat[stepId]];
      if (currentToolResults[stepId] && currentToolResults[stepId].length) {
        newCurrentChat = currentToolResults[stepId];
      }
      const stepChats = [...chats[stepId], ...newCurrentChat];

      const data = {
        session_id: sessionId,
        step_id: stepId,
        chats: stepChats,
        bot_id: botId,
        isTest,
      };

      const updatedStepChats = [...chats[stepId]]; // Local variable to hold the updated conversations
      let delay = 1000;
      let attempt = 0;
      const maxRetries = 5;

      const retryStream = async () => {
        setIsBusy(true);
        try {
          const streamResponse = await initializeStepStream(data, userGoogleId);

          // Stream the step chat
          const streamId = streamResponse.stream_id;
          const eventSource = streamStepChat(streamId, userGoogleId);

          setCurrentToolResults({ ...currentToolResults, [stepId]: [] });
          setBotStreamingResponse({
            ...botStreamingResponse,
            [stepId]: { role: 'assistant', content: '', tool_calls: [] },
          });

          let toolCalls = {}; // To store tool calls by index (0, 1, etc.)

          // Construct the final response message
          let currentStreamingResponseMessage = {
            role: 'assistant',
            tool_calls: [],
            content: '',
          };

          eventSource.onmessage = async (event) => {
            const chunk = JSON.parse(event.data);

            for (const choice of chunk.choices) {
              const content = choice.delta?.content || '';
              if (choice.finish_reason) {
                eventSource.close();
                // setIsBusy(false);

                for (const chat of newCurrentChat) {
                  // await addConversationToTask(currentTaskId, chat);
                  await saveStepChat(
                    botId,
                    stepId,
                    userGoogleId,
                    sessionId,
                    chat,
                  );
                }
                await saveStepChat(
                  botId,
                  stepId,
                  userGoogleId,
                  sessionId,
                  currentStreamingResponseMessage,
                );

                setBotCurrentChat({
                  ...botCurrentChat,
                  [stepId]: { role: 'user', content: '' },
                });

                setBotStreamingResponse({
                  ...botStreamingResponse,
                  [stepId]: { role: 'assistant', content: '', tool_calls: [] },
                });

                if (
                  savedStepFinalResponse &&
                  currentStreamingResponseMessage.content &&
                  !currentStreamingResponseMessage.tool_calls.length
                ) {
                  setChats({
                    ...chats,
                    [stepId]: [
                      ...updatedStepChats,
                      ...newCurrentChat,
                      currentStreamingResponseMessage,
                      {
                        role: 'bot',
                        type: 'step_response',
                        content: stepsFinalResponse[stepId],
                      },
                    ],
                  });

                  await saveStepChat(botId, stepId, userGoogleId, sessionId, {
                    role: 'bot',
                    content: stepsFinalResponse[stepId],
                    type: 'step_response',
                  });
                  if (currentStreamingResponseMessage.content) {
                    // ("Just set the step completed", stepCompleted);
                    setSavedStepFinalResponse(false);
                    setStepCompleted(false);
                    setStepCompleted(true);
                  }
                } else {
                  setChats({
                    ...chats,
                    [stepId]: [
                      ...updatedStepChats,
                      ...newCurrentChat,
                      currentStreamingResponseMessage,
                    ],
                  });
                }

                setIsBusy(false);
                scrollToBottom();
                setError(null);
              } else if (content) {
                currentStreamingResponseMessage.content += content;
                setBotStreamingResponse({
                  ...botStreamingResponse,
                  [stepId]: currentStreamingResponseMessage,
                });
                scrollToBottom();
              } else if (choice.delta?.tool_calls) {
                choice.delta.tool_calls.forEach((toolCall) => {
                  const index = toolCall.index;
                  if (index !== undefined) {
                    if (!toolCalls[index]) {
                      // Initialize the tool call details if not already present
                      currentStreamingResponseMessage.tool_calls.push({
                        id: toolCall.id || '',
                        function: {
                          arguments: '',
                          name: '', // Initialize an empty name
                        },
                        type: 'function',
                      });
                      // setStreamingResponse(currentStreamingResponseMessage);
                      setBotStreamingResponse({
                        ...botStreamingResponse,
                        [stepId]: currentStreamingResponseMessage,
                      });
                      toolCalls[index] = {
                        id: toolCall.id || '',
                      };
                    }

                    // Append the arguments as they come in chunks
                    if (toolCall.function && toolCall.function.arguments) {
                      currentStreamingResponseMessage.tool_calls[
                        index
                      ].function.arguments += toolCall.function.arguments;
                      setBotStreamingResponse({
                        ...botStreamingResponse,
                        [stepId]: currentStreamingResponseMessage,
                      });
                    }

                    // Handle the function name if it's present in this chunk
                    if (toolCall.function && toolCall.function.name) {
                      currentStreamingResponseMessage.tool_calls[
                        index
                      ].function.name = toolCall.function.name;
                      setBotStreamingResponse({
                        ...botStreamingResponse,
                        [stepId]: currentStreamingResponseMessage,
                      });
                    }
                  }
                });
              }
            }
          };

          eventSource.onerror = async (err) => {
            setIsBusy(false);
            eventSource.close();

            if (attempt < maxRetries) {
              attempt += 1;
              delay = delay * 2; // Exponential backoff
              await new Promise((resolve) => setTimeout(resolve, delay));
              retryStream(); // Retry the stream
            } else {
              setError('Max retries reached. Please try again later.');
            }
          };

          eventSource.onclose = () => {
            this.setState({ loading: false });
            setIsBusy(false);
          };
        } catch (err) {
          setError(JSON.stringify(err));
          setIsBusy(false);
        }
      };
      retryStream();
    } catch (err) {
      setError(JSON.stringify(err));
    }
  };

  useEffect(() => {
    const handleAutomaticToolUse = async () => {
      if (isAutomatic && currentStepId) {
        const stepChats = chats[currentStepId];

        if (!stepChats.length) {
          return;
        }

        const lastChat = stepChats[stepChats.length - 1];
        if (lastChat && lastChat.tool_calls && lastChat.tool_calls.length > 0) {
          let allHandled = true;
          let tool_responses = [];
          for (const tool_call of lastChat.tool_calls) {
            allHandled = false; // Set to false if there is at least one unhandled tool_use
            const data = await handleFunctionCall(tool_call, currentStepId);
            if (!data) {
              return;
            }
            tool_responses.push(data);
          }
          if (!allHandled) {
            setCurrentToolResults({
              ...currentToolResults,
              [currentStepId]: tool_responses,
            });
            setHaveStepResults(true); // Only set hasTaskResults to true when all tool_uses are handled
          }
        }
      }
    };

    handleAutomaticToolUse();
    // eslint-disable-next-line
  }, [chats, isAutomatic]);

  const handleFunctionCall = async (tool, stepId) => {
    try {
      let tempToolDict = {};
      if (tool.function.name === 'save_final_response') {
        tempToolDict['final_response_markdown'] = JSON.parse(
          tool.function.arguments,
        ).final_response_markdown;
      } else if (tool.function.name === 'save_user_feedback') {
        tempToolDict['feedback'] = JSON.parse(tool.function.arguments).feedback;
      } else if (tool.function.name === 'log_error') {
        tempToolDict['error_message'] = JSON.parse(
          tool.function.arguments,
        ).error_message;
      }

      setIsBusy(true);
      const data = await callFunction(
        {
          id: tool.id,
          name: tool.function.name,
          arguments: tool.function.arguments,
          type: tool.type,
          task_id: currentTaskId,
          bot_id: selectedBotId,
          is_bot: true,
          isTest,
        },
        userGoogleId,
      );

      if (data.error) {
        setError(data.error);
        setIsBusy(false);
        return false;
      } else {
        if (!data.is_error && tempToolDict['final_response_markdown']) {
          setSavedStepFinalResponse(true);
          setStepsFinalResponse({
            ...stepsFinalResponse,
            [stepId]: tempToolDict['final_response_markdown'],
          });
        }

        setError(null);
        setIsBusy(false);
        return data;
      }
    } catch (error) {
      toast.error(error);
      setError('An error occurred while calling the function.');
      setIsBusy(false);
      return false;
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <DataContext.Provider
      value={{
        botId,
        steps,
        loading,
        error,
        chatInput,
        chats,
        botCurrentChat,
        botStreamingResponse,
        responses,
        sessionId,
        currentStepId,
        currentStepIndex,
        isAutomatic,
        isAutomaticSwitching,
        haveStepResults,
        isTest,
        stepCompleted,
        savedStepFinalResponse,
        stepsFinalResponse,

        // Setter functions
        setBotId,
        setSteps,
        setLoading,
        setError,
        setChatInput,
        setChats,
        setBotCurrentChat,
        setBotStreamingResponse,
        setResponses,
        setSessionId,
        setCurrentStepId,
        setCurrentStepIndex,
        setIsAutomatic,
        setIsAutomaticSwitching,
        setHaveStepResults,
        setIsTest,
        setStepCompleted,
        setSavedStepFinalResponse,
        setStepsFinalResponse,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const BotsDataState = () => {
  return useContext(DataContext);
};

export default BotsDataProvider;
